import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type HTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

const avatarVariants = cva(
  [
    'inline-flex items-center justify-center select-none rounded-full overflow-hidden',
    'font-mono uppercase font-semibold tracking-wide',
    'bg-surface-3 text-text',
  ].join(' '),
  {
    variants: {
      size: {
        sm: 'h-6 w-6 text-[10px]',
        md: 'h-7 w-7 text-[11px]',
        lg: 'h-9 w-9 text-xs',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
);

function deriveInitials(input: string): string {
  const trimmed = input.trim();
  if (trimmed.length === 0) return '?';
  const parts = trimmed.split(/\s+/);
  if (parts.length === 1) {
    return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  }
  const first = parts[0] ?? '';
  const last = parts[parts.length - 1] ?? '';
  return `${first.charAt(0)}${last.charAt(0)}`.toUpperCase();
}

export interface AvatarProps
  extends HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof avatarVariants> {
  name: string;
  initials?: string;
}

export const Avatar = forwardRef<HTMLSpanElement, AvatarProps>(
  ({ className, size, name, initials, ...props }, ref) => {
    const display = initials ?? deriveInitials(name);
    return (
      <span
        ref={ref}
        role="img"
        aria-label={name}
        className={cn(avatarVariants({ size }), className)}
        {...props}
      >
        {display}
      </span>
    );
  },
);
Avatar.displayName = 'Avatar';

export { avatarVariants };
